import { API_BASE_URL } from '@/api/client'

/**
 * Download a file from an authenticated endpoint.
 *
 * `apiRequest` is JSON-only, so every surface offering a PDF or a CSV that the *server* renders has
 * to reach for `fetch` itself. The invoice PDF button did exactly that inline; the Buchhaltung
 * report needs the same two downloads, which is the second occurrence and therefore the moment the
 * helper is worth having rather than a third copy of the same twelve lines.
 *
 * The server's `Content-Disposition` filename wins when it sends one: the name is part of the
 * document ("Tagesbericht-2026-09-11.pdf" is how the reader files it), and the endpoint knows the
 * period it just rendered better than the caller does. `fallbackFileName` covers a proxy that
 * strips the header.
 */
export async function downloadAuthenticated(
  path: string,
  fallbackFileName: string,
): Promise<void> {
  // Imported at call time, as the invoice button did: the token is only needed when somebody
  // actually clicks, and a page offering a download should not pull the auth store into its chunk.
  const { useAuthStore } = await import('@/features/auth/store/auth-store')
  const token = useAuthStore.getState().accessToken
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error(`download_${res.status}`)

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download =
    fileNameFromDisposition(res.headers.get('Content-Disposition')) ??
    fallbackFileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoked late rather than immediately: Safari starts the download after the click returns, and a
  // URL revoked in the same tick leaves it with nothing to fetch.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * The filename out of a `Content-Disposition` header, or null.
 *
 * `filename*` (RFC 5987, percent-encoded UTF-8) is preferred over the plain `filename`, because the
 * plain one is where a German umlaut arrives mangled.
 */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim())
    } catch {
      // A malformed encoding is not worth failing the download over.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header)
  return plain ? plain[1].trim() : null
}
